import { createApiWithNewBasePath } from "@api";

import {
  IUserSession,
  getRefusalCode,
  storeAuthToken,
} from "./emailLogin";
import { TSsoProvider } from "./ssoProviders";

/**
 * `POST /api/public/users/sso_sessions` (RT-4365), which both signs an existing
 * identity in and opens an account for one it has never seen — the visitor is
 * never asked which of the two they meant.
 */

export interface ICreateSsoSessionInput {
  provider: TSsoProvider;
  /** Google/Facebook: the OAuth access token from `signInWithProvider`. */
  token?: string;
  /** Apple: selects the backend's web verification branch. */
  os?: "web";
  /** Apple: the identity JWT from the popup, verified server-side. */
  id_token?: string;
  /** Sent on the retry only, once the missing-info form has supplied them. */
  firstname?: string;
  lastname?: string;
  /** E.164, and omitted rather than sent empty. */
  phone_number?: string;
  /** Sent on the retry only, when the provider withheld an email and the visitor typed one. */
  email?: string;
  /** ISO 3166-1 alpha-2. Absent falls back to the request's own country. */
  country_code?: string;
  /** Attribution source (`getKnowFrom`); absent when the visitor came in untracked. */
  know_from?: string;
}

/** Every refusal `Sso::AuthenticateUser` names that this flow can reach. */
export type TSsoSessionError =
  | "provider_not_supported"
  | "no_email_received"
  | "email_banished"
  | "temporarily_banished"
  | "firstname_missing"
  | "lastname_missing"
  | "phone_number_invalid"
  | "registration_failed";

/**
 * Answers with the same `{ user, token }` the email login does, so the draft's
 * claim POST that follows is authenticated the same way.
 */
export const createSsoSession = async (
  input: ICreateSsoSessionInput
): Promise<IUserSession> => {
  const { data } = await createApiWithNewBasePath.post<
    ICreateSsoSessionInput,
    IUserSession
  >("public/users/sso_sessions", input);

  storeAuthToken(data.token);

  return data;
};

export const getSsoSessionError = getRefusalCode<TSsoSessionError>;

/**
 * A refusal the missing-info form can act on rather than one to report as a
 * failure: a name or an email the provider withheld, or a phone number its own
 * retry supplied and the endpoint rejected. Reported one field at a time, so
 * any of them opens or keeps open the same form.
 */
export const isRecoverableSsoError = (code?: TSsoSessionError): boolean =>
  code === "firstname_missing" ||
  code === "lastname_missing" ||
  code === "no_email_received" ||
  code === "phone_number_invalid";
